import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 50, left: 55 };
const PRICE_HEIGHT = 120;
const VELOCITY_HEIGHT = 130;
const ACCEL_HEIGHT = 130;
const GAP = 20;
const TOTAL_HEIGHT = PRICE_HEIGHT + GAP + VELOCITY_HEIGHT + GAP + ACCEL_HEIGHT;

const COLORS = {
  priceLine: '#2563eb',
  priceFill: '#2563eb',
  velocityPositive: '#16a34a',
  velocityNegative: '#dc2626',
  accelLine: '#7c3aed',
  accelFillPositive: '#7c3aed',
  accelFillNegative: '#7c3aed',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function PriceAccelerationChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const safeData = useMemo(() => {
    if (!data || !data.dates || data.dates.length === 0) return null;
    if (!data.velocity || !data.acceleration || !data.price) return null;
    return data;
  }, [data]);

  useEffect(() => {
    if (!safeData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const priceInnerH = PRICE_HEIGHT - MARGIN.top - 10;
    const velInnerH = VELOCITY_HEIGHT - 10 - 10;
    const accelInnerH = ACCEL_HEIGHT - 10 - MARGIN.bottom;

    svg
      .attr('width', width)
      .attr('height', TOTAL_HEIGHT)
      .attr('viewBox', `0 0 700 450`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    // ──────────────────────────────────────────
    // Shared X scale
    // ──────────────────────────────────────────
    const x = d3.scalePoint()
      .domain(safeData.dates)
      .range([0, innerW])
      .padding(0.5);

    // ──────────────────────────────────────────
    // TOP PANEL — Price
    // ──────────────────────────────────────────
    const priceG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    priceG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Price');

    const priceExtent = d3.extent(safeData.price.filter((v) => v != null));
    const pricePadding = ((priceExtent[1] || 0) - (priceExtent[0] || 0)) * 0.15 || 1;

    const yPrice = d3.scaleLinear()
      .domain([(priceExtent[0] || 0) - pricePadding, (priceExtent[1] || 0) + pricePadding])
      .nice()
      .range([priceInnerH, 0]);

    // Grid lines
    const priceTicks = yPrice.ticks(4);
    priceG.selectAll('.price-grid')
      .data(priceTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yPrice(d))
      .attr('y2', (d) => yPrice(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Y axis
    priceG.append('g')
      .call(
        d3.axisLeft(yPrice)
          .ticks(4)
          .tickSize(0)
          .tickFormat((v) => `$${v.toFixed(0)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    priceG.selectAll('.domain').attr('stroke', '#e5e7eb');
    priceG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Price area + line
    const pricePoints = safeData.dates
      .map((date, i) => ({ date, value: safeData.price[i] }))
      .filter((d) => d.value != null);

    if (pricePoints.length > 1) {
      const areaGen = d3.area()
        .x((d) => x(d.date))
        .y0(priceInnerH)
        .y1((d) => yPrice(d.value))
        .curve(d3.curveMonotoneX);

      priceG.append('path')
        .datum(pricePoints)
        .attr('d', areaGen)
        .attr('fill', COLORS.priceFill)
        .attr('fill-opacity', 0.08);

      const lineGen = d3.line()
        .x((d) => x(d.date))
        .y((d) => yPrice(d.value))
        .curve(d3.curveMonotoneX);

      priceG.append('path')
        .datum(pricePoints)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', COLORS.priceLine)
        .attr('stroke-width', 2);
    }

    // ──────────────────────────────────────────
    // MIDDLE PANEL — Velocity (bar chart)
    // ──────────────────────────────────────────
    const velTopY = PRICE_HEIGHT + GAP;
    const velG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${velTopY})`);

    velG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Velocity');

    const velValues = safeData.velocity.filter((v) => v != null);
    const velMax = d3.max(velValues, (v) => Math.abs(v)) || 1;
    const velPadding = velMax * 0.15;

    const yVel = d3.scaleLinear()
      .domain([-(velMax + velPadding), velMax + velPadding])
      .nice()
      .range([velInnerH, 0]);

    // Grid lines
    const velTicks = yVel.ticks(4);
    velG.selectAll('.vel-grid')
      .data(velTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yVel(d))
      .attr('y2', (d) => yVel(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    velG.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yVel(0))
      .attr('y2', yVel(0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1);

    // Y axis
    velG.append('g')
      .call(
        d3.axisLeft(yVel)
          .ticks(4)
          .tickSize(0)
          .tickFormat((v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    velG.selectAll('.domain').attr('stroke', '#e5e7eb');
    velG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Velocity bars
    const barSpacing = innerW / safeData.dates.length;
    const barWidth = Math.max(2, barSpacing * 0.5);

    safeData.dates.forEach((date, i) => {
      const val = safeData.velocity[i];
      if (val == null) return;

      const cx = x(date);
      const isPositive = val >= 0;
      const barTop = isPositive ? yVel(val) : yVel(0);
      const barBottom = isPositive ? yVel(0) : yVel(val);
      const barHeight = Math.max(1, barBottom - barTop);

      velG.append('rect')
        .attr('x', cx - barWidth / 2)
        .attr('y', barTop)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', isPositive ? COLORS.velocityPositive : COLORS.velocityNegative)
        .attr('fill-opacity', 0.75)
        .attr('rx', 1);
    });

    // ──────────────────────────────────────────
    // BOTTOM PANEL — Acceleration (line + area + phase bg)
    // ──────────────────────────────────────────
    const accelTopY = PRICE_HEIGHT + GAP + VELOCITY_HEIGHT + GAP;
    const accelG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${accelTopY})`);

    accelG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Acceleration');

    const accelValues = safeData.acceleration.filter((v) => v != null);
    const accelMax = d3.max(accelValues, (v) => Math.abs(v)) || 1;
    const accelPadding = accelMax * 0.15;

    const yAccel = d3.scaleLinear()
      .domain([-(accelMax + accelPadding), accelMax + accelPadding])
      .nice()
      .range([accelInnerH, 0]);

    // Phase background shading
    if (safeData.phases && safeData.phases.length > 0) {
      safeData.phases.forEach((phase) => {
        const startDate = safeData.dates[phase.startIdx];
        const endDate = safeData.dates[Math.min(phase.endIdx, safeData.dates.length - 1)];
        if (!startDate || !endDate) return;

        const x0 = x(startDate) - barSpacing / 2;
        const x1 = x(endDate) + barSpacing / 2;

        accelG.append('rect')
          .attr('x', Math.max(0, x0))
          .attr('y', 0)
          .attr('width', Math.min(innerW, x1) - Math.max(0, x0))
          .attr('height', accelInnerH)
          .attr('fill', phase.color || '#e5e7eb')
          .attr('fill-opacity', 0.12);

        // Phase label at top
        const midX = (Math.max(0, x0) + Math.min(innerW, x1)) / 2;
        accelG.append('text')
          .attr('x', midX)
          .attr('y', 10)
          .attr('text-anchor', 'middle')
          .attr('font-size', '8px')
          .attr('fill', phase.color || '#6b7280')
          .attr('fill-opacity', 0.7)
          .text(phase.label);
      });
    }

    // Grid lines
    const accelTicks = yAccel.ticks(4);
    accelG.selectAll('.accel-grid')
      .data(accelTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yAccel(d))
      .attr('y2', (d) => yAccel(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    accelG.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yAccel(0))
      .attr('y2', yAccel(0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1);

    // X axis (only on bottom panel)
    accelG.append('g')
      .attr('transform', `translate(0, ${accelInnerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(formatDate))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    accelG.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    accelG.append('g')
      .call(
        d3.axisLeft(yAccel)
          .ticks(4)
          .tickSize(0)
          .tickFormat((v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    accelG.selectAll('.domain').attr('stroke', '#e5e7eb');
    accelG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Acceleration area fill to zero
    const accelPoints = safeData.dates
      .map((date, i) => ({ date, value: safeData.acceleration[i] }))
      .filter((d) => d.value != null);

    if (accelPoints.length > 1) {
      // Split into positive and negative areas for distinct coloring
      const areaAbove = d3.area()
        .x((d) => x(d.date))
        .y0(yAccel(0))
        .y1((d) => yAccel(Math.max(0, d.value)))
        .curve(d3.curveMonotoneX);

      const areaBelow = d3.area()
        .x((d) => x(d.date))
        .y0(yAccel(0))
        .y1((d) => yAccel(Math.min(0, d.value)))
        .curve(d3.curveMonotoneX);

      accelG.append('path')
        .datum(accelPoints)
        .attr('d', areaAbove)
        .attr('fill', COLORS.accelFillPositive)
        .attr('fill-opacity', 0.1);

      accelG.append('path')
        .datum(accelPoints)
        .attr('d', areaBelow)
        .attr('fill', COLORS.accelFillNegative)
        .attr('fill-opacity', 0.1);

      // Acceleration line
      const accelLineGen = d3.line()
        .x((d) => x(d.date))
        .y((d) => yAccel(d.value))
        .curve(d3.curveMonotoneX);

      accelG.append('path')
        .datum(accelPoints)
        .attr('d', accelLineGen)
        .attr('fill', 'none')
        .attr('stroke', COLORS.accelLine)
        .attr('stroke-width', 2);

      // Dots
      accelPoints.forEach((d) => {
        accelG.append('circle')
          .attr('cx', x(d.date))
          .attr('cy', yAccel(d.value))
          .attr('r', 2.5)
          .attr('fill', COLORS.accelLine)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);
      });
    }

    // ──────────────────────────────────────────
    // CROSSHAIR + HOVER (spanning all 3 panels)
    // ──────────────────────────────────────────
    const crosshairPrice = priceG.append('line')
      .attr('y1', 0)
      .attr('y2', priceInnerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const crosshairVel = velG.append('line')
      .attr('y1', 0)
      .attr('y2', velInnerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const crosshairAccel = accelG.append('line')
      .attr('y1', 0)
      .attr('y2', accelInnerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Hover dots
    const priceDot = priceG.append('circle')
      .attr('r', 4)
      .attr('fill', COLORS.priceLine)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none');

    const accelDot = accelG.append('circle')
      .attr('r', 4)
      .attr('fill', COLORS.accelLine)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none');

    // Helper to find closest date index from mouse x
    function findClosestIdx(mx) {
      const domainDates = safeData.dates;
      let closestIdx = 0;
      let closestDist = Infinity;
      domainDates.forEach((date, i) => {
        const dist = Math.abs(x(date) - mx);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      return closestIdx;
    }

    function handleHover(event, panelG) {
      const [mx] = d3.pointer(event, panelG.node());
      const idx = findClosestIdx(mx);
      const cx = x(safeData.dates[idx]);

      // Show crosshairs on all panels
      crosshairPrice.attr('x1', cx).attr('x2', cx).style('display', null);
      crosshairVel.attr('x1', cx).attr('x2', cx).style('display', null);
      crosshairAccel.attr('x1', cx).attr('x2', cx).style('display', null);

      // Price dot
      const priceVal = safeData.price[idx];
      if (priceVal != null) {
        priceDot.attr('cx', cx).attr('cy', yPrice(priceVal)).style('display', null);
      } else {
        priceDot.style('display', 'none');
      }

      // Acceleration dot
      const accelVal = safeData.acceleration[idx];
      if (accelVal != null) {
        accelDot.attr('cx', cx).attr('cy', yAccel(accelVal)).style('display', null);
      } else {
        accelDot.style('display', 'none');
      }

      // Find current phase
      let currentPhase = null;
      if (safeData.phases) {
        for (const phase of safeData.phases) {
          if (idx >= phase.startIdx && idx <= phase.endIdx) {
            currentPhase = phase.label;
            break;
          }
        }
      }

      setTooltip({
        x: event.clientX + 14,
        y: event.clientY - 10,
        date: safeData.dates[idx],
        price: priceVal,
        velocity: safeData.velocity[idx],
        acceleration: accelVal,
        phase: currentPhase,
      });
    }

    function handleLeave() {
      crosshairPrice.style('display', 'none');
      crosshairVel.style('display', 'none');
      crosshairAccel.style('display', 'none');
      priceDot.style('display', 'none');
      accelDot.style('display', 'none');
      setTooltip(null);
    }

    // Invisible overlays for each panel
    priceG.append('rect')
      .attr('width', innerW)
      .attr('height', priceInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event, priceG))
      .on('mouseleave', () => handleLeave());

    velG.append('rect')
      .attr('width', innerW)
      .attr('height', velInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event, velG))
      .on('mouseleave', () => handleLeave());

    accelG.append('rect')
      .attr('width', innerW)
      .attr('height', accelInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event, accelG))
      .on('mouseleave', () => handleLeave());

    return () => {
      svg.selectAll('*').remove();
    };
  }, [safeData]);

  if (!safeData) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Acceleration Analysis</div>
          <div style={styles.subtitle}>Velocity (rate of change) and acceleration (change in velocity) of average prices</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Acceleration Analysis</div>
        <div style={styles.subtitle}>Velocity (rate of change) and acceleration (change in velocity) of average prices</div>
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 3, background: COLORS.priceLine, borderRadius: 1 }} />
          <span>Price</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 8, height: 10, background: COLORS.velocityPositive, borderRadius: 1, opacity: 0.75 }} />
          <span>Velocity +</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 8, height: 10, background: COLORS.velocityNegative, borderRadius: 1, opacity: 0.75 }} />
          <span>Velocity -</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 3, background: COLORS.accelLine, borderRadius: 1 }} />
          <span>Acceleration</span>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {(() => {
              const date = new Date(tooltip.date);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            })()}
          </div>
          {tooltip.price != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Price</span>
              <span style={{ fontWeight: 600, color: COLORS.priceLine }}>
                ${tooltip.price.toFixed(2)}
              </span>
            </div>
          )}
          {tooltip.velocity != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Velocity</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.velocity >= 0 ? COLORS.velocityPositive : COLORS.velocityNegative,
                }}
              >
                {tooltip.velocity >= 0 ? '+' : ''}{tooltip.velocity.toFixed(3)}
              </span>
            </div>
          )}
          {tooltip.acceleration != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Acceleration</span>
              <span
                style={{
                  fontWeight: 600,
                  color: COLORS.accelLine,
                }}
              >
                {tooltip.acceleration >= 0 ? '+' : ''}{tooltip.acceleration.toFixed(3)}
              </span>
            </div>
          )}
          {tooltip.phase && (
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Phase</span>
                <span style={{ fontWeight: 600, color: '#374151' }}>
                  {tooltip.phase}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
